import { Component, Ref } from 'vue-property-decorator'
import ConfigurationSectionHeader from '@/modules/configurationModule/ConfigurationSectionHeader.vue'
import VtGrid from '@/components/VtGrid/VtGrid.vue'
import { GetContextMenuItemsParams, MenuItemDef } from '@ag-grid-enterprise/all-modules'
import AssaysModal from '@/modules/configurationModule/detail/automation/assays/components/assays-modal/configuration-automation-assays-modal.vue'
import { BModal } from 'bootstrap-vue'
import BaseCrudServerSideComponent from '@/shared/resources/base-crud-server-side.component'
import { BaseCrud } from '@/shared/models/cruds.models'
import { EntityIdentifiersRequest, EntityIdentifierWithErrorResponse } from '@/shared/models/http/http.model'
import { ConfigurationAutomaticOntologyAssayResponse } from '@/modules/configurationModule/detail/automation/assays/services/configuration-automatic-ontology-assay/configuration-automatic-ontology-assay.dto'
import { ConfigurationAutomationAssaysGridState, initialConfigurationAutomationAssaysGridState } from '@/modules/configurationModule/detail/automation/assays/components/assays-grid/configuration-automation-assays-grid.model'
import { ConfigurationOntologyForAutomaticAssayResponse } from '@/modules/configurationModule/detail/automation/assays/services/configuration-ontology-for-automatic-assay/configuration-ontology-for-automatic-assay.dto'

@Component({
    components: {
        ConfigurationSectionHeader,
        VtGrid,
        AssaysModal
    }
})
export default class ConfigurationAutomationAssaysGrid extends BaseCrudServerSideComponent<ConfigurationOntologyForAutomaticAssayResponse> implements BaseCrud {
    @Ref() readonly assaysModalWrapper!: typeof BModal

    state: ConfigurationAutomationAssaysGridState

    get canUpdate(): boolean {
        return this.state.selectedRows.length === 1
    }

    get canDelete(): boolean {
        return !!this.state.selectedRows.length
    }

    constructor() {
        super()
        this.state = initialConfigurationAutomationAssaysGridState()
    }

    /// SERVICES CALLS ///

    private async _deleteAutomaticOntologyAssays(requestDelete: EntityIdentifiersRequest): Promise<EntityIdentifierWithErrorResponse[] | null> {
        const ids = requestDelete?.entityIdentifiers
        if (ids?.length) {
            const response = await this.$_toCallUnique(this.state.service.delete(this.state.laboratoryId, requestDelete))
            if (response) {
                const elements = ids.length - response.length
                if (elements > 0) {
                    const choice = elements > 1 ? 2 : 1
                    this.$_showToastSuccess(this.$tc('configuration.toastMessages.deleted', choice, { elements }))
                }
                return response
            }
        }
        return null
    }

    /// GRID OPERATIONS ///

    private async _showModalAdviceWhenDelete(): Promise<boolean> {
        const labelConfirmation: string = this.$tc('configuration.grid.deleteConfirmation')
        const selectedRowsSize: number = this.state?.selectedRows?.length
        const labelGrid: string = this.$tc('configuration.grid.element', selectedRowsSize)
        const labelQuestion: string = this.$tc('configuration.grid.deleteQuestion')
        const labelAcceptAction: string = this.$tc('configuration.actions.delete')
        return this.$_showConfirmationModal(
            `${labelConfirmation} ${selectedRowsSize} ${labelGrid}, <br/> ${labelQuestion}`,
            labelAcceptAction
        )
    }

    async deleteOperation(requestDelete: EntityIdentifiersRequest): Promise<void> {
        if (requestDelete) {
            if (await this._showModalAdviceWhenDelete()) {
                if (await this._deleteAutomaticOntologyAssays(requestDelete)) {
                    this.reloadData()
                }
            }
        }
    }

    /// GRID EVENT HANDLERS ///
    onUpdate(automaticOntologyAssay: ConfigurationAutomaticOntologyAssayResponse = this.state.gridApi?.getSelectedRows()[0]): void {
        if (automaticOntologyAssay) {
            this.showModal(this.$tc('configuration.actions.update'), automaticOntologyAssay, this.assaysModalWrapper)
        }
    }

    async onDelete(): Promise<void> {
        if (this.state.selectedRows.length) {
            const requestDelete: EntityIdentifiersRequest = {
                entityIdentifiers: this.state.selectedRows.map(automaticOntologyAssay => automaticOntologyAssay.id)
            }
            await this.deleteOperation(requestDelete)
        }
    }

    onAdd(): void {
        this.showModal(this.$tc('configuration.actions.add'), null, this.assaysModalWrapper)
    }

    getContextMenuItems(params: GetContextMenuItemsParams): MenuItemDef[] {
        return [
            {
                name: this.$tc('configuration.actions.update'),
                cssClasses: this.state.selectedRows.length > 1 ? ['not-show'] : [''],
                action: () => this.onUpdate(params?.node?.data)
            },
            {
                name: this.$tc('configuration.actions.delete'),
                cssClasses: this.state.selectedRows.length < 1 ? ['not-show'] : [''],
                action: this.onDelete
            }
        ]
    }
}
